import uuid from '@form-engine/core-util/uuid';
import { FormEngineElementController } from '@form-engine/core-application/hooks/use-element';

export interface UploadState {
    id: string;
    attachmentIndex?: { attachmentId: string; uploadUrl: string };
    bsoCategory: string;
    file: File;
    loadState: 'initial' | 'indexed' | 'uploaded';
    error?: Error;
}

export class FileSelectionSizeError extends Error {
    constructor() {
        super('The files selected are too big');
    }
}

export class FileSelectionCancelError extends Error {
    constructor() {
        super('File selection canceled');
    }
}

export class FileSelectionEmptyError extends Error {
    constructor() {
        super('No valid files to upload');
    }
}

export class UnexpectedUploadLoadStateError extends Error {
    constructor(need: UploadState['loadState'], got: UploadState['loadState']) {
        super(`load state '${need}' required, got ${got}`);
    }
}

export function selectFile(
    accept = '*/*',
    maxSize = 0,
    multiple = false
): Promise<FileList | null> {
    const fileInput = document.createElement('input');

    fileInput.type = 'file';
    fileInput.accept = accept;
    fileInput.multiple = multiple;

    return new Promise((resolve, reject) => {
        fileInput.onchange = () => {
            if (fileInput.files && maxSize) {
                const size = Array.from(fileInput.files).reduce(
                    (p, c) => p + c.size,
                    0
                );
                if (size > maxSize * 1024 * 1024) {
                    reject(new FileSelectionSizeError());
                    return;
                }
            }

            resolve(fileInput.files);
        };
        fileInput.oncancel = () => reject(new FileSelectionCancelError());
        fileInput.click();
    });
}

export function createAttachmentIndices(
    controller: FormEngineElementController,
    files: FileList
) {
    const indices = [];

    for (const file of files) {
        indices.push(
            controller({
                action: 'createAttachmentIndex',
                fileName: file.name,
                fileSize: file.size,
            })
        );
    }

    return Promise.all(indices);
}

export async function uploadFileTo(presignedUploadUrl: string, file: File) {
    if (document.location.host === 'localhost') {
        console.info('localdev environment detected, simulating upload...');
        await new Promise((resolve) =>
            setTimeout(resolve, 1500 + Math.random() * 500)
        );
    } else {
        await fetch(presignedUploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': file.type },
            body: file,
        });
    }
}

export async function requestUserFile(
    bsoCategory: string,
    accept?: string,
    maxSize?: number
): Promise<UploadState> {
    const files = await selectFile(accept, maxSize);

    if (!files?.length) {
        throw new FileSelectionEmptyError();
    }

    return {
        id: uuid(),
        file: files.item(0)!,
        bsoCategory,
        loadState: 'initial',
    };
}

export async function requestAttachmentIndexForUploadState(
    controller: FormEngineElementController,
    state: UploadState
): Promise<UploadState> {
    const { file, bsoCategory, loadState } = state;

    if (loadState !== 'initial') {
        throw new UnexpectedUploadLoadStateError('initial', loadState);
    }

    return {
        ...state,
        attachmentIndex: await controller({
            action: 'createAttachmentIndex',
            fileName: file.name,
            category: bsoCategory,
            fileSize: file.size,
        }),
        loadState: 'indexed',
    };
}

export async function uploadAttachmentForUploadState(
    state: UploadState
): Promise<UploadState> {
    const { file, attachmentIndex, loadState } = state;

    if (!attachmentIndex || loadState !== 'indexed') {
        throw new UnexpectedUploadLoadStateError('indexed', loadState);
    }

    await uploadFileTo(attachmentIndex.uploadUrl, file);

    return {
        ...state,
        loadState: 'uploaded',
    };
}
